import { useState } from 'react';
import { useNavigate } from 'react-router';
import { type ApiKey, type ApiRequestSummary, api } from '../api';
import { LogsIcon, SearchIcon } from '../components/icons';
import {
  ErrorNote,
  formatDate,
  PageHeader,
  SkeletonRows,
  Status,
  timeAgo,
  useDebounced,
} from '../components/ui';
import { useLoad } from '../hooks';

const RANGES = [
  { days: 1, label: 'Last 24 hours' },
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
];

type Page = { data: ApiRequestSummary[]; hasMore: boolean };

export function StatusCode({ code }: { code: number }) {
  const tone = code < 300 ? 'ok' : code < 500 ? 'warn' : 'bad';
  return <Status status={String(code)} tone={tone} />;
}

export function Method({ method }: { method: string }) {
  return <span className={`method method-${method.toLowerCase()}`}>{method}</span>;
}

export function Logs() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(7);
  const [status, setStatus] = useState('');
  const [method, setMethod] = useState('');
  const [apiKeyId, setApiKeyId] = useState('');
  const q = useDebounced(search.trim());
  const [older, setOlder] = useState<ApiRequestSummary[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const query = (before?: string) => {
    const p = new URLSearchParams({ limit: '50', days: String(days) });
    if (q) p.set('search', q);
    if (status) p.set('status', status);
    if (method) p.set('method', method);
    if (apiKeyId) p.set('apiKeyId', apiKeyId);
    if (before) p.set('before', before);
    return `/logs?${p}`;
  };

  const first = useLoad(async () => {
    const res = await api<Page>('GET', query());
    setOlder([]);
    setHasMore(res.hasMore);
    return res.data;
  }, [q, days, status, method, apiKeyId]);
  const keys = useLoad(() => api<{ data: ApiKey[] }>('GET', '/api-keys'), []);

  const rows = [...(first.data ?? []), ...older];
  const filtered = Boolean(q || status || method || apiKeyId || days !== 7);

  async function loadMore() {
    const last = rows.at(-1);
    if (!last) return;
    setLoadingMore(true);
    try {
      const res = await api<Page>('GET', query(last.id));
      setOlder((o) => [...o, ...res.data]);
      setHasMore(res.hasMore);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Logs"
        description="Every request your API keys made in the last 30 days."
      />
      <ErrorNote error={first.error} />

      <div className="filters">
        <div className="search">
          <SearchIcon />
          <input
            className="input"
            type="search"
            placeholder="Search by path or ID, e.g. /emails or em_…"
            aria-label="Search logs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="input"
          aria-label="Time range"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {RANGES.map((r) => (
            <option key={r.days} value={r.days}>
              {r.label}
            </option>
          ))}
        </select>
        <select
          className="input"
          aria-label="Result"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All results</option>
          <option value="success">Succeeded (2xx)</option>
          <option value="error">Failed (4xx, 5xx)</option>
        </select>
        <select
          className="input hide-sm"
          aria-label="Method"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
        >
          <option value="">All methods</option>
          {['GET', 'POST', 'PATCH', 'DELETE', 'SMTP'].map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>
        <select
          className="input hide-sm"
          aria-label="API key"
          value={apiKeyId}
          onChange={(e) => setApiKeyId(e.target.value)}
        >
          <option value="">All API keys</option>
          {(keys.data?.data ?? []).map((k) => (
            <option key={k.id} value={k.id}>
              {k.name}
            </option>
          ))}
        </select>
      </div>

      {!first.loading && rows.length === 0 ? (
        <div className="empty" style={{ padding: '56px 24px' }}>
          <span className="tile">{filtered ? <SearchIcon /> : <LogsIcon />}</span>
          <h3>{filtered ? 'No requests match' : 'No API requests yet'}</h3>
          <p>
            {filtered
              ? 'Try another search, a longer time range or different filters.'
              : 'Requests made with your API keys show up here within a second, including the ones that failed.'}
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>Status</th>
                <th className="hide-sm">API key</th>
                <th className="hide-sm nowrap">Duration</th>
                <th className="nowrap">When</th>
              </tr>
            </thead>
            <tbody>
              {first.loading ? (
                <SkeletonRows cols={5} />
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className="row-link"
                    tabIndex={0}
                    onClick={() => navigate(`/logs/${r.id}`)}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/logs/${r.id}`)}
                  >
                    <td className="truncate">
                      <Method method={r.method} /> <span className="mono">{r.path}</span>
                    </td>
                    <td>
                      <StatusCode code={r.statusCode} />
                    </td>
                    <td className="hide-sm muted truncate">{r.apiKeyName ?? 'Deleted key'}</td>
                    <td className="hide-sm muted nowrap">{r.durationMs} ms</td>
                    <td className="muted nowrap" title={formatDate(r.createdAt)}>
                      {timeAgo(r.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 ? (
        <div className="table-foot">
          <span>
            {rows.length} request{rows.length === 1 ? '' : 's'}
            {hasMore ? ' loaded' : ''}
          </span>
          {hasMore ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={loadingMore}
              onClick={() => void loadMore()}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
